import React, {useEffect, useState} from 'react';
import {FaBuilding} from 'react-icons/fa';
import {AppDispatch, RootState} from 'store/store';
import {useDispatch, useSelector} from 'react-redux';
import TextInput from 'components/inputs/TextInput';
import {Company} from 'types/company';
import {CompanyThunks} from 'features/company/companyThunks';
import PhoneNumberInput from 'components/inputs/PhoneNumberInput';
import {showToast} from 'components/ToastContainer';
import {setIsEditing} from 'features/company/companySlice';
import XIcon from 'assets/images/icons/XIcon';
import SubmitButton from 'components/SubmitButton';
import DescriptionInput from 'components/inputs/DescriptionInput';
import {Autocomplete, useLoadScript} from '@react-google-maps/api';

interface CompanyEditFormProps {
  onClose: () => void;
}

const GOOGLE_MAPS_LIBRARIES = ['places'];

const CompanyEditForm: React.FC<CompanyEditFormProps> = ({onClose}) => {
  const company = useSelector((state: RootState) => state.company.company);
  const dispatch = useDispatch<AppDispatch>();
  const [phoneNumber, setPhoneNumber] = useState(company?.phoneNumber);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const {isLoaded} = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES as any,
  });
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [formData, setFormData] = useState({
    ...company,
  });

  useEffect(() => {
    const loadScript = async () => {
      await dispatch(CompanyThunks.getCurrent());
    };
    loadScript();
  }, []);

  useEffect(() => {
    setFormData({
      ...company,
    });
    setPhoneNumber(company?.phoneNumber);
  }, [company]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const {id, value} = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleClose = () => {
    dispatch(setIsEditing(false));
    onClose();
  };

  const onPlaceChanged = () => {
    if (autocomplete && autocomplete.getPlace()) {
      const place = autocomplete.getPlace();
      setFormData((prevData: any) => ({
        ...prevData,
        address: place.formatted_address || '',
      }));
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setLogoLoading(true);
        await dispatch(
          CompanyThunks.update(
            new Company({
              ...formData,
              logo: file,
            }),
          ),
        )
          .then((resultAction: any) => {
            setFormData((prevData: any) => ({
              ...prevData,
              logoUrl: resultAction.payload.logoUrl,
            }));
          })
          .finally(() => {
            setLogoLoading(false);
          });
      } catch (error) {
        setLogoLoading(false);
        showToast('Failed to update company logo.', 'error');
      }
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(
        CompanyThunks.update(
          new Company({
            ...formData,
            deleteLogo: true,
          }),
        ),
      ).then(() => {
        setFormData((prevData: any) => ({
          ...prevData,
          logoUrl: '',
        }));
      });
    } catch (error) {
      showToast('Failed to delete company logo.', 'error');
    }
  };

  const handleClickOutsideMaps = () => {
    const element = document.querySelector('.pac-container');
    if (element) {
      element.setAttribute('style', 'display: none');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await dispatch(CompanyThunks.update(new Company(formData)));
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              Company profile
            </div>

            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              Update your company information
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{display: 'none'}}
          accept="image/*"
          onChange={handleFileChange}
        />
        <div className="p-4 sm:p-7 px-4 sm:px-6 w-full">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 mt-1 bg-slate-100 rounded-full flex justify-center items-center overflow-hidden">
                {formData?.logoUrl ? (
                  <img
                    src={formData.logoUrl}
                    alt="Company Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FaBuilding className="text-gray-400 size-8" />
                )}
              </div>
              <button
                type="button"
                disabled={logoLoading}
                className="py-2.5 px-3 w-auto inline-flex justify-center items-center gap-x-2 text-sm font-medium
                           rounded-lg border border-transparent bg-cyan-400 text-white hover:bg-cyan-500
                           disabled:opacity-50
                           disabled:pointer-events-none dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-neutral-600"
                onClick={() => fileInputRef.current?.click()}>
                Upload New Logo
              </button>
              <button
                type="button"
                className={`py-2.5 px-3 w-auto inline-flex justify-center items-center gap-x-2 text-sm font-medium
                          rounded-lg text-slate-500 bg-white
                          disabled:pointer-events-none dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-neutral-600 ${
                            !formData?.logoUrl
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                onClick={handleDelete}
                disabled={!formData?.logoUrl || logoLoading}>
                Delete
              </button>
            </div>
            <div className="flex flex-col space-y-6 items-center">
              <div className="w-full mt-6">
                <TextInput
                  id="name"
                  type="text"
                  label="Company Name"
                  value={formData.name || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="w-full">
                {isLoaded && (
                  <Autocomplete
                    onLoad={autocomplete => setAutocomplete(autocomplete)}
                    onPlaceChanged={onPlaceChanged}
                    onUnmount={() => setAutocomplete(null)}>
                    <TextInput
                      id="address"
                      type="text"
                      label="Address"
                      value={formData.address || ''}
                      onChange={handleChange}
                      onBlur={handleClickOutsideMaps}
                    />
                  </Autocomplete>
                )}
              </div>
              <div className="w-full">
                <PhoneNumberInput
                  id="phoneNumber"
                  label="Phone Number"
                  value={formData.phoneNumber || ''}
                  onChange={handleChange}
                  phoneNumber={phoneNumber}
                  setPhoneNumber={() => {}}
                />
              </div>
              <div className="w-full">
                <DescriptionInput
                  id="slogan"
                  type="text"
                  label="Slogan"
                  value={formData.slogan || ''}
                  onChange={handleChange}
                  optional="optional"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-2 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label="Update company"
              disabled={false}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="button"
              onClick={handleClose}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompanyEditForm;
